import type { ReactElement } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type {
  BillingEnforcementPolicy,
  BillingEnforcementState,
} from '../../services/billingEnforcementService';
import { billingProfileManagementPath } from '../../constants/billingProfilePaths';
import {
  BILLING_SCROLL_TO_MANAGE_LOCATION_STATE,
  handleBillingManagementLinkClick,
} from '../../utils/billingManagementLink';

const LINK_CLASS =
  'font-medium underline underline-offset-2 hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 rounded-sm';

/** Tailwind z-index for fixed billing banner (above app chrome, documented for stack consistency). */
const BANNER_Z_INDEX_CLASS = 'z-[70]';

function titleForState(state: BillingEnforcementState): string {
  switch (state) {
    case 'past_due':
      return 'Payment overdue';
    case 'grace_period':
      return 'Billing grace period';
    case 'limited_service':
      return 'Limited service mode';
    case 'suspended':
      return 'Account suspended';
    default:
      return 'Billing notice';
  }
}

function messageForState(state: BillingEnforcementState): string {
  switch (state) {
    case 'past_due':
      return 'Your recent payment did not complete. Full access is still active while we retry payment.';
    case 'grace_period':
      return 'Your account is in grace period. AI remains active, and write-heavy operations may be throttled.';
    case 'limited_service':
      return 'AI inference is disabled and write operations are blocked until billing is resolved.';
    case 'suspended':
      return 'Access is restricted to billing and data export actions until payment is completed.';
    default:
      return 'Your account has a billing notice.';
  }
}

function styleForState(state: BillingEnforcementState): {
  box: string;
  text: string;
  link: string;
} {
  switch (state) {
    case 'past_due':
      return {
        box: 'border-blue-200 bg-blue-50',
        text: 'text-blue-950',
        link: 'text-blue-900 focus-visible:ring-blue-500 focus-visible:ring-offset-blue-50',
      };
    case 'grace_period':
      return {
        box: 'border-amber-200 bg-amber-50',
        text: 'text-amber-950',
        link: 'text-amber-900 focus-visible:ring-amber-500 focus-visible:ring-offset-amber-50',
      };
    case 'limited_service':
      return {
        box: 'border-orange-200 bg-orange-50',
        text: 'text-orange-950',
        link: 'text-orange-900 focus-visible:ring-orange-500 focus-visible:ring-offset-orange-50',
      };
    case 'suspended':
      return {
        box: 'border-red-200 bg-red-50',
        text: 'text-red-950',
        link: 'text-red-900 focus-visible:ring-red-500 focus-visible:ring-offset-red-50',
      };
    default:
      return {
        box: 'border-gray-200 bg-gray-50',
        text: 'text-gray-950',
        link: 'text-gray-900 focus-visible:ring-gray-500 focus-visible:ring-offset-gray-50',
      };
  }
}

export function BillingEnforcementBanner(props: {
  policy: BillingEnforcementPolicy;
  isOrgUser: boolean;
}): ReactElement | null {
  const location = useLocation();

  if (props.policy.state === 'active') {
    return null;
  }
  const styles = styleForState(props.policy.state);
  const billingPath = billingProfileManagementPath(props.isOrgUser);

  return (
    <div
      className={`fixed top-2 left-1/2 ${BANNER_Z_INDEX_CLASS} w-[min(96vw,900px)] -translate-x-1/2`}
    >
      <div
        className={`rounded-lg border px-4 py-3 shadow-sm ${styles.box} ${styles.text}`}
        role='status'
      >
        <p className='m-0'>
          <span className='font-semibold'>{titleForState(props.policy.state)}.</span>{' '}
          {messageForState(props.policy.state)}
        </p>
        {typeof props.policy.days_until_suspension === 'number' &&
        props.policy.state !== 'suspended' ? (
          <p className='mt-1 m-0 text-sm opacity-90'>
            Full account suspension in {props.policy.days_until_suspension} day
            {props.policy.days_until_suspension === 1 ? '' : 's'} if not resolved. Your data is
            kept; there is no deletion.
          </p>
        ) : null}
        <p className='mt-1 m-0'>
          <Link
            to={billingPath}
            state={BILLING_SCROLL_TO_MANAGE_LOCATION_STATE}
            className={`${LINK_CLASS} ${styles.link}`}
            onClick={(e) => handleBillingManagementLinkClick(e, location.pathname, billingPath)}
          >
            Open billing
          </Link>{' '}
          to restore full access.
        </p>
      </div>
    </div>
  );
}
